"use strict";

window.vimeoApiReady = function () {
  theme.config.vimeoLoading = true;

  // Because there's no way to check for the Vimeo API being loaded
  // asynchronously, we use this terrible timeout to wait for it being ready
  checkIfVimeoIsReady().then(function () {
    theme.config.vimeoReady = true;
    theme.config.vimeoLoading = false;
    document.dispatchEvent(new CustomEvent("vimeoReady"));
  });
};

function checkIfVimeoIsReady() {
  var wait;
  var timeout;

  var deferred = new Promise((resolve, reject) => {
    wait = setInterval(function () {
      if (!Vimeo) {
        return;
      }

      clearInterval(wait);
      clearTimeout(timeout);
      resolve();
    }, 500);

    timeout = setTimeout(function () {
      clearInterval(wait);
      reject();
    }, 4000); // subjective. test up to 8 times over 4 seconds
  });

  return deferred;
}

theme.VimeoPlayer = (function () {
  var classes = {
    loading: "loading",
    loaded: "loaded",
    interactable: "video-interactable",
  };

  var defaults = {
    background: true,
    byline: false,
    controls: false,
    loop: true,
    muted: true,
    playsinline: true,
    portrait: false,
    title: false,
  };

  function VimeoPlayer(divId, videoId, options) {
    this.divId = divId;
    this.el = document.getElementById(divId);
    this.videoId = videoId;
    this.iframe = null;
    this.options = options;

    if (this.options && this.options.videoParent) {
      this.parent = this.el.closest(this.options.videoParent);
    }

    this.setAsLoading();

    if (theme.config.vimeoReady) {
      this.init();
    } else {
      theme.LibraryLoader.load("vimeo", window.vimeoApiReady);
      document.addEventListener("vimeoReady", this.init.bind(this));
    }
  }

  VimeoPlayer.prototype = Object.assign({}, VimeoPlayer.prototype, {
    init: function () {
      var args = defaults;
      args.id = this.videoId;

      this.videoPlayer = new Vimeo.Player(this.el, args);

      this.videoPlayer.ready().then(this.playerReady.bind(this));
    },

    playerReady: function () {
      this.iframe = this.el.querySelector("iframe");
      this.iframe.setAttribute("tabindex", "-1");

      this.videoPlayer.setMuted(true);

      this.setAsLoaded();

      // pause when out of view
      var observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.play();
            } else {
              this.pause();
            }
          });
        },
        { rootMargin: "0px 0px 50px 0px" },
      );

      observer.observe(this.iframe);
    },

    setAsLoading: function () {
      if (!this.parent) return;
      this.parent.classList.add(classes.loading);
    },

    setAsLoaded: function () {
      if (!this.parent) return;
      this.parent.classList.remove(classes.loading);
      this.parent.classList.add(classes.loaded);
      if (Shopify && Shopify.designMode) {
        if (window.AOS) {
          AOS.refreshHard();
        }
      }
    },

    enableInteraction: function () {
      if (!this.parent) return;
      this.parent.classList.add(classes.interactable);
    },

    play: function () {
      if (this.videoPlayer && typeof this.videoPlayer.play === "function") {
        this.videoPlayer.play();
      }
    },

    pause: function () {
      if (this.videoPlayer && typeof this.videoPlayer.pause === "function") {
        this.videoPlayer.pause();
      }
    },

    destroy: function () {
      if (
        this.videoPlayer &&
        typeof this.videoPlayer.destroy === "function"
      ) {
        this.videoPlayer.destroy();
      }
    },
  });

  return VimeoPlayer;
})();

window.onYouTubeIframeAPIReady = function () {
  theme.config.youTubeReady = true;
  document.dispatchEvent(new CustomEvent("youTubeReady"));
};

/*============================================================================
  YouTube SDK method
  Parameters:
    - player div id (required)
    - arguments
      - videoId (required)
      - videoParent (selector, optional for section loading state)
      - events (object, optional)
==============================================================================*/
theme.YouTube = (function () {
  var classes = {
    loading: "loading",
    loaded: "loaded",
    interactable: "video-interactable",
  };

  var defaults = {
    width: 1280,
    height: 720,
    playerVars: {
      autohide: 0,
      autoplay: 1,
      cc_load_policy: 0,
      controls: 0,
      fs: 0,
      iv_load_policy: 3,
      modestbranding: 1,
      playsinline: 1,
      rel: 0,
    },
  };

  function YouTube(divId, options) {
    this.divId = divId;
    this.iframe = null;

    this.attemptedToPlay = false;

    // API callback events
    defaults.events = {
      onReady: this.onVideoPlayerReady.bind(this),
      onStateChange: this.onVideoStateChange.bind(this),
    };

    this.options = Object.assign({}, defaults, options);

    if (this.options) {
      if (this.options.videoParent) {
        this.parent = document
          .getElementById(this.divId)
          .closest(this.options.videoParent);
      }

      // Most YT videos will autoplay. If in product media,
      // will handle in theme.Product instead
      if (!this.options.autoplay) {
        this.options.playerVars.autoplay = this.options.autoplay;
      }

      if (this.options.style === "sound") {
        this.options.playerVars.controls = 1;
        this.options.playerVars.autoplay = 0;
      }
    }

    this.setAsLoading();

    if (theme.config.youTubeReady) {
      this.init();
    } else {
      theme.LibraryLoader.load("youtubeSdk");
      document.addEventListener("youTubeReady", this.init.bind(this));
    }
  }

  YouTube.prototype = Object.assign({}, YouTube.prototype, {
    init: function () {
      this.videoPlayer = new YT.Player(this.divId, this.options);
    },

    onVideoPlayerReady: function (evt) {
      this.iframe = document.getElementById(this.divId); // iframe once YT loads
      this.iframe.setAttribute("tabindex", "-1");

      if (this.options.style !== "sound") {
        evt.target.mute();
      }

      // pause when out of view
      var observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.play();
            } else {
              this.pause();
            }
          });
        },
        { rootMargin: "0px 0px 50px 0px" },
      );

      observer.observe(this.iframe);
    },

    onVideoStateChange: function (evt) {
      switch (evt.data) {
        case -1: // unstarted
          // Handle low power state on iOS by checking if
          // video is reset to unplayed after attempting to buffer
          if (this.attemptedToPlay) {
            this.setAsLoaded();
            this.enableInteraction();
          }
          break;
        case 0: // ended, loop it
          this.play(evt);
          break;
        case 1: // playing
          this.setAsLoaded();
          break;
        case 3: // buffering
          this.attemptedToPlay = true;
          break;
      }
    },

    setAsLoading: function () {
      if (!this.parent) return;
      this.parent.classList.add(classes.loading);
    },

    setAsLoaded: function () {
      if (!this.parent) return;
      this.parent.classList.remove(classes.loading);
      this.parent.classList.add(classes.loaded);
      if (Shopify && Shopify.designMode) {
        if (window.AOS) {
          AOS.refreshHard();
        }
      }
    },

    enableInteraction: function () {
      if (!this.parent) return;
      this.parent.classList.add(classes.interactable);
    },

    play: function () {
      if (
        this.videoPlayer &&
        typeof this.videoPlayer.playVideo === "function"
      ) {
        this.videoPlayer.playVideo();
      }
    },

    pause: function () {
      if (
        this.videoPlayer &&
        typeof this.videoPlayer.pauseVideo === "function"
      ) {
        this.videoPlayer.pauseVideo();
      }
    },

    destroy: function () {
      if (
        this.videoPlayer &&
        typeof this.videoPlayer.destroy === "function"
      ) {
        this.videoPlayer.destroy();
      }
    },
  });

  return YouTube;
})();

// Prevent vertical scroll while using flickity sliders
(function () {
  var e = !1;
  var t;

  document.body.addEventListener("touchstart", function (i) {
    if (!i.target.closest(".flickity-slider")) {
      return (e = !1);
      void 0;
    }
    e = !0;
    t = {
      x: i.touches[0].pageX,
      y: i.touches[0].pageY,
    };
  });

  document.body.addEventListener(
    "touchmove",
    function (i) {
      if (e && i.cancelable) {
        var n = {
          x: i.touches[0].pageX - t.x,
          y: i.touches[0].pageY - t.y,
        };
        Math.abs(n.x) > Flickity.defaults.dragThreshold && i.preventDefault();
      }
    },
    { passive: !1 },
  );
})();

/**
 * Ajax Renderer
 * -----------------------------------------------------------------------------
 * Render sections without reloading the page.
 * @param {Object[]} sections - The section to update on render.
 * @param {string} sections[].sectionId - The ID of the section from Shopify.
 * @param {string} sections[].nodeId - The ID of the DOM node to replace.
 * @param {Function} sections[].onReplace (optional) - The custom render function.
 * @param {boolean} debug - Output logs to console for debugging.
 *
 */

theme.videoModal = function () {
  var youtubePlayer;

  var videoHolderId = "VideoHolder";
  var selectors = {
    youtube: 'a[href*="youtube.com/watch"], a[href*="youtu.be/"]',
    mp4Trigger: ".product-video-trigger--mp4",
    mp4Player: ".product-video-mp4-sound",
  };

  var youtubeTriggers = document.querySelectorAll(selectors.youtube);
  var mp4Triggers = document.querySelectorAll(selectors.mp4Trigger);

  if (!youtubeTriggers.length && !mp4Triggers.length) {
    return;
  }

  var videoHolderDiv = document.getElementById(videoHolderId);

  if (youtubeTriggers.length) {
    theme.LibraryLoader.load("youtubeSdk");
  }

  var modal = new theme.Modals("VideoModal", "video-modal", {
    closeOffContentClick: true,
    bodyOpenClass: ["modal-open", "video-modal-open"],
    solid: true,
  });

  youtubeTriggers.forEach((btn) => {
    btn.addEventListener("click", triggerYouTubeModal);
  });

  mp4Triggers.forEach((btn) => {
    btn.addEventListener("click", triggerMp4Modal);
  });

  document.addEventListener("modalClose.VideoModal", closeVideoModal);

  function triggerYouTubeModal(evt) {
    // If not already loaded, treat as normal link
    if (!theme.config.youTubeReady) {
      return;
    }

    evt.preventDefault();
    emptyVideoHolder();

    modal.open(evt);

    var videoId = getYoutubeVideoId(evt.currentTarget.getAttribute("href"));
    youtubePlayer = new theme.YouTube(videoHolderId, {
      videoId: videoId,
      style: "sound",
      events: {
        onReady: onYoutubeReady,
      },
    });
  }

  function triggerMp4Modal(evt) {
    emptyVideoHolder();

    var el = evt.currentTarget;
    var player = el.parentNode.querySelector(selectors.mp4Player);

    // Clone video element and place it in the modal
    var playerClone = player.cloneNode(true);
    playerClone.classList.remove("hide");

    videoHolderDiv.append(playerClone);
    modal.open(evt);

    // Play new video element
    videoHolderDiv.querySelector("video").play();
  }

  function onYoutubeReady(evt) {
    evt.target.unMute();
    evt.target.playVideo();
  }

  function getYoutubeVideoId(url) {
    var regExp =
      /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/;
    var match = url.match(regExp);
    return match && match[7].length == 11 ? match[7] : false;
  }

  function emptyVideoHolder() {
    videoHolderDiv.innerHTML = "";
  }

  function closeVideoModal() {
    if (youtubePlayer && typeof youtubePlayer.destroy === "function") {
      youtubePlayer.destroy();
    } else {
      emptyVideoHolder();
    }
  }
};

/*============================================================================
  ToolTip
==============================================================================*/

theme.VideoSection = (function () {
  var selectors = {
    videoParent: ".video-parent-section",
  };

  function videoSection(container) {
    this.container = container;
    this.sectionId = container.getAttribute("data-section-id");
    this.namespace = ".video-" + this.sectionId;
    this.videoObject;

    theme.initWhenVisible({
      element: this.container,
      callback: this.init.bind(this),
      threshold: 500,
    });
  }

  videoSection.prototype = Object.assign({}, videoSection.prototype, {
    init: function () {
      var dataDiv = this.container.querySelector(".video-div");
      if (!dataDiv) {
        return;
      }
      var type = dataDiv.dataset.type;

      switch (type) {
        case "youtube":
          var videoId = dataDiv.dataset.videoId;
          this.initYoutubeVideo(videoId);
          break;
        case "vimeo":
          var videoId = dataDiv.dataset.videoId;
          this.initVimeoVideo(videoId);
          break;
        case "mp4":
          this.initMp4Video();
          break;
      }
    },

    initYoutubeVideo: function (videoId) {
      this.videoObject = new theme.YouTube("YouTubeVideo-" + this.sectionId, {
        videoId: videoId,
        videoParent: selectors.videoParent,
      });
    },

    initVimeoVideo: function (videoId) {
      this.videoObject = new theme.VimeoPlayer(
        "Vimeo-" + this.sectionId,
        videoId,
        {
          videoParent: selectors.videoParent,
        },
      );
    },

    initMp4Video: function () {
      var mp4Video = "Mp4Video-" + this.sectionId;
      var mp4Div = document.getElementById(mp4Video);
      var parent = mp4Div.closest(selectors.videoParent);

      if (mp4Div) {
        parent.classList.add("loaded");

        var playPromise = document.querySelector("#" + mp4Video).play();

        // Edge does not return a promise (video still plays)
        if (playPromise !== undefined) {
          playPromise
            .then(function () {
              // playback normal
            })
            .catch(function () {
              mp4Div.setAttribute("controls", "");
              parent.classList.add("video-interactable");
            });
        }
      }
    },

    onUnload: function (evt) {
      var sectionId = evt.target.id.replace("shopify-section-", "");
      if (
        this.videoObject &&
        typeof this.videoObject.destroy === "function"
      ) {
        this.videoObject.destroy();
      }
    },
  });

  return videoSection;
})();

theme.Photoswipe = (function () {
  var selectors = {
    trigger: ".js-photoswipe__zoom",
    images: ".photoswipe__image",
    slideshowTrack: ".flickity-viewport ",
    activeImage: ".is-selected",
  };

  function Photoswipe(container, sectionId) {
    this.container = container;
    this.sectionId = sectionId;
    this.namespace = ".photoswipe-" + this.sectionId;
    this.gallery;
    this.images;
    this.items;
    this.inSlideshow = false;

    if (!container || container.dataset.zoom === "false") {
      return;
    }

    this.init();
  }

  Photoswipe.prototype = Object.assign({}, Photoswipe.prototype, {
    init: function () {
      this.container
        .querySelectorAll(selectors.trigger)
        .forEach((trigger) => {
          trigger.on("click" + this.namespace, this.triggerClick.bind(this));
        });
    },

    triggerClick: function (evt) {
      if (
        this.container.dataset &&
        this.container.dataset.hasSlideshow === "true"
      ) {
        this.inSlideshow = true;
      } else {
        this.inSlideshow = false;
      }

      this.items = this.getImageData();

      var image = this.inSlideshow
        ? this.container.querySelector(selectors.activeImage)
        : evt.currentTarget;

      var index = this.inSlideshow
        ? this.getChildIndex(image)
        : image.dataset.index;

      this.initGallery(this.items, index);
    },

    // Because of image set feature, need to get index based on location in parent
    getChildIndex: function (el) {
      var i = 0;
      while ((el = el.previousSibling) != null) {
        i++;
      }

      // 1-based index required
      return i + 1;
    },

    getImageData: function () {
      this.images = this.inSlideshow
        ? this.container.querySelectorAll(
            selectors.slideshowTrack + selectors.images,
          )
        : this.container.querySelectorAll(selectors.images);

      var items = [];
      var options = {};

      this.images.forEach((el) => {
        var item = {
          msrc: el.currentSrc || el.src,
          src: el.getAttribute("data-photoswipe-src"),
          w: el.getAttribute("data-photoswipe-width"),
          h: el.getAttribute("data-photoswipe-height"),
          el: el,
          initialZoomLevel: 0.5,
        };

        items.push(item);
      });

      return items;
    },

    initGallery: function (items, index) {
      var pswpElement = document.querySelectorAll(".pswp")[0];

      var options = {
        allowPanToNext: false,
        captionEl: false,
        closeOnScroll: false,
        counterEl: false,
        history: false,
        index: index - 1,
        pinchToClose: false,
        preloaderEl: false,
        scaleMode: "zoom",
        shareEl: false,
        tapToToggleControls: false,
        getThumbBoundsFn: function (index) {
          var pageYScroll =
            window.pageYOffset || document.documentElement.scrollTop;
          var thumbnail = items[index].el;
          var rect = thumbnail.getBoundingClientRect();
          return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
        },
      };

      this.gallery = new PhotoSwipe(
        pswpElement,
        PhotoSwipeUI_Default,
        items,
        options,
      );
      this.gallery.listen("afterChange", this.afterChange.bind(this));
      this.gallery.init();

      this.preventiOS15Scrolling();
    },

    afterChange: function () {
      var index = this.gallery.getCurrentIndex();
      this.container.dispatchEvent(
        new CustomEvent("photoswipe:afterChange", {
          detail: {
            index: index,
          },
        }),
      );
    },

    syncHeight: function () {
      document.documentElement.style.setProperty(
        "--window-inner-height",
        `${window.innerHeight}px`,
      );
    },

    // Fix poached from https://gist.github.com/dimsemenov/0b8c255c0d87f2989e8ab876073534ea
    preventiOS15Scrolling: function () {
      let initialScrollPos;

      if (!/iPhone|iPad|iPod/i.test(window.navigator.userAgent)) return;

      this.syncHeight();

      // Store scroll position to restore it later
      initialScrollPos = window.scrollY;

      // Add class to root element when PhotoSwipe opens
      document.documentElement.classList.add("pswp-open-in-ios");

      window.addEventListener("resize", this.syncHeight);

      this.gallery.listen("destroy", () => {
        document.documentElement.classList.remove("pswp-open-in-ios");
        window.scrollTo(0, initialScrollPos);
      });
    },
  });

  return Photoswipe;
})();

theme.ProductMedia = (function () {
  var modelJsonSections = {};
  var models = {};
  var xrButtons = {};

  var selectors = {
    mediaGroup: "[data-product-single-media-group]",
    xrButton: "[data-shopify-xr]",
  };

  function init(modelViewerContainers, sectionId) {
    modelJsonSections[sectionId] = {
      loaded: false,
    };

    modelViewerContainers.forEach(function (container, index) {
      var mediaId = container.dataset.mediaId;
      var modelViewerElement = container.querySelector("model-viewer");
      var modelId = modelViewerElement.dataset.modelId;

      if (index === 0) {
        var mediaGroup = container.closest(selectors.mediaGroup);
        var xrButton = mediaGroup.querySelector(selectors.xrButton);
        xrButtons[sectionId] = {
          element: xrButton,
          defaultId: modelId,
        };
      }

      models[mediaId] = {
        modelId: modelId,
        sectionId: sectionId,
        container: container,
        element: modelViewerElement,
      };
    });

    window.Shopify.loadFeatures([
      {
        name: "shopify-xr",
        version: "1.0",
        onLoad: setupShopifyXr,
      },
      {
        name: "model-viewer-ui",
        version: "1.0",
        onLoad: setupModelViewerUi,
      },
    ]);

    theme.LibraryLoader.load("modelViewerUiStyles");
  }

  function setupShopifyXr(errors) {
    if (errors) return;

    if (!window.ShopifyXR) {
      document.addEventListener("shopify_xr_initialized", function () {
        setupShopifyXr();
      });
      return;
    }

    for (var sectionId in modelJsonSections) {
      if (modelJsonSections.hasOwnProperty(sectionId)) {
        var modelSection = modelJsonSections[sectionId];

        if (modelSection.loaded) continue;

        var modelJson = document.querySelector("#ModelJson-" + sectionId);

        window.ShopifyXR.addModels(JSON.parse(modelJson.innerHTML));
        modelSection.loaded = true;
      }
    }
    window.ShopifyXR.setupXRElements();
  }

  function setupModelViewerUi(errors) {
    if (errors) return;

    for (var key in models) {
      if (models.hasOwnProperty(key)) {
        var model = models[key];
        if (!model.modelViewerUi && Shopify) {
          model.modelViewerUi = new Shopify.ModelViewerUI(model.element);
        }
        setupModelViewerListeners(model);
      }
    }
  }

  function setupModelViewerListeners(model) {
    var xrButton = xrButtons[model.sectionId];

    model.container.addEventListener("mediaVisible", function () {
      xrButton.element.setAttribute("data-shopify-model3d-id", model.modelId);
      if (theme.config.isTouch) return;
      model.modelViewerUi.play();
    });

    model.container.addEventListener("mediaHidden", function () {
      xrButton.element.setAttribute(
        "data-shopify-model3d-id",
        xrButton.defaultId,
      );
      model.modelViewerUi.pause();
    });

    model.container.addEventListener("xrLaunch", function () {
      model.modelViewerUi.pause();
    });
  }

  function removeSectionModels(sectionId) {
    for (var key in models) {
      if (models.hasOwnProperty(key)) {
        var model = models[key];
        if (model.sectionId === sectionId) {
          delete models[key];
        }
      }
    }
    delete modelJsonSections[sectionId];
  }

  return {
    init: init,
    removeSectionModels: removeSectionModels,
  };
})();

